import { Check } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { MarketStatusBadge } from '@/components/widget/market-status-badge'
import type { MarketType } from '@/features/dashboard/components/watchlist-wizard/at-filter-match'
import { marketTypeOf } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-grouping'
import { SUPPORTED_EXCHANGE_LABELS } from '@/features/dashboard/lib/supported-exchanges'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { highlightMatch } from '@/lib/highlight-match'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { Instrument } from '@/types/generated/common/types'

// Product-type chip: exchange-standard abbreviations, so not translated.
const MARKET_TYPE_CHIP: Record<MarketType, { label: string; className: string }> = {
  spot: { label: 'SPOT', className: 'border-cat-green/50 text-cat-green' },
  perpetual: { label: 'PERP', className: 'border-cat-blue/50 text-cat-blue' },
  future: { label: 'FUTURE', className: 'border-cat-yellow/50 text-cat-yellow' },
  options: { label: 'OPTION', className: 'border-cat-violet/50 text-cat-violet' },
}

interface WatchlistInstrumentRowProps {
  instrument: Instrument
  indentation: 'type' | 'expiry' | 'detail'
  alreadyAdded: boolean
  checked: boolean
  highlighted: boolean
  singleMode: boolean
  searching: boolean
  query: string
  exchangeFallbackLabel: string
  price?: string
  priceLoading?: boolean
  changePercent?: number
  /** Overrides the bold title normally derived from `instrument.symbol` —
   * used for a collapsed options representative row (e.g. "BTC_USDC"). */
  titleOverride?: string
  /** Overrides the subtitle normally derived from the enrichment display
   * name — paired with `titleOverride`, e.g. "Bitcoin Options". */
  subtitleOverride?: string
  /** Names what `price` is when it isn't a price (e.g. "ATM IV"); shown under it in place
   * of the 24h change. */
  priceCaption?: string
  /** Hover text on the value — e.g. which contract an ATM IV was read from. */
  priceTitle?: string
  onActivate: (instrument: Instrument) => void
}

export function WatchlistInstrumentRow({
  instrument,
  indentation,
  alreadyAdded,
  checked,
  highlighted,
  singleMode,
  searching,
  query,
  exchangeFallbackLabel,
  price,
  priceLoading = false,
  changePercent,
  titleOverride,
  subtitleOverride,
  priceCaption,
  priceTitle,
  onActivate,
}: WatchlistInstrumentRowProps) {
  const t = useTranslations('watchlistWizard')
  const key = watchlistEntryKey(instrument.exchange, instrument.symbol)
  const exchangeIconKey = getExchangeIconKey(instrument.exchange)
  const symbolContent = titleOverride
    ? titleOverride
    : searching
      ? highlightMatch(formatSymbol(instrument.symbol), query)
      : formatSymbol(instrument.symbol)
  const displayNameContent = titleOverride
    ? (subtitleOverride ?? formatSymbol(instrument.symbol))
    : searching && instrument.enrichment?.displayName
      ? highlightMatch(instrument.enrichment.displayName, query)
      : instrument.enrichment?.displayName
  const selected = !singleMode && checked
  // 'expiry' rows already sit under a venue/quote section header — repeating
  // the exchange icon+label on every leaf row was redundant and pushed the
  // symbol far to the right for no reason.
  const showExchangeColumn = indentation !== 'expiry'
  const hasChange = changePercent !== undefined && Number.isFinite(changePercent)
  const marketType = marketTypeOf(instrument)
  const typeChip = marketType ? MARKET_TYPE_CHIP[marketType] : null
  const venueLabel = SUPPORTED_EXCHANGE_LABELS.get(instrument.exchange) ?? exchangeFallbackLabel

  return (
    <button
      type="button"
      onClick={() => onActivate(instrument)}
      className={cn(
        'relative flex min-h-11 w-full items-center gap-3 overflow-hidden pr-5 text-left',
        indentation === 'detail' ? 'px-4' : indentation === 'expiry' ? 'pl-14' : 'pl-20',
        'cursor-pointer',
        selected ? 'bg-white/5 hover:bg-white/5' : 'hover:bg-foreground/4',
        highlighted && !selected && 'bg-foreground/4'
      )}
      data-row-key={key}
      data-keyboard-row-key={key}
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="bg-emphasis pointer-events-none absolute inset-y-0 left-0 w-0.5"
        />
      ) : null}
      {showExchangeColumn ? (
        <span className="text-foreground/55 flex w-32 shrink-0 items-center gap-2 truncate text-xs font-medium">
          {exchangeIconKey ? (
            <ExchangeIcon exchange={exchangeIconKey} className="size-5 shrink-0 rounded-full" />
          ) : (
            <span className="size-5 shrink-0 rounded-full bg-white/[0.06]" />
          )}
          <span className="truncate">{venueLabel}</span>
          <MarketStatusBadge exchange={instrument.exchange} className="size-3.5 shrink-0" />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-foreground/90 truncate text-sm font-medium">{symbolContent}</span>
          {typeChip ? (
            <span
              className={cn(
                'inline-flex h-4 shrink-0 items-center border px-1 text-[10px] font-medium uppercase',
                typeChip.className
              )}
            >
              {/* Rows under a venue section header have no exchange column; name it here. */}
              {showExchangeColumn ? typeChip.label : `${venueLabel} · ${typeChip.label}`}
            </span>
          ) : null}
        </span>
        {displayNameContent ? (
          <span className="text-foreground/40 block truncate text-xs">{displayNameContent}</span>
        ) : null}
      </span>
      {priceLoading ? (
        <span
          className="flex w-16 shrink-0 animate-pulse flex-col items-end gap-1 motion-reduce:animate-none"
          aria-hidden="true"
        >
          <span className="bg-foreground/10 h-2.5 w-14" />
          <span className="bg-foreground/7 h-2 w-9" />
        </span>
      ) : price ? (
        <span className="flex w-16 shrink-0 flex-col items-end" title={priceTitle}>
          <span className="text-foreground/80 text-xs font-medium tabular-nums">{price}</span>
          {priceCaption ? (
            <span className="text-foreground/40 text-[10px] whitespace-nowrap">{priceCaption}</span>
          ) : hasChange ? (
            <span
              className={cn(
                'text-[10px] tabular-nums',
                changePercent >= 0 ? 'text-chart-mint' : 'text-chart-red'
              )}
            >
              {changePercent >= 0 ? '+' : ''}
              {changePercent.toFixed(2)}%
            </span>
          ) : null}
        </span>
      ) : null}
      {alreadyAdded ? (
        <Check className="text-foreground size-4 shrink-0" aria-label={t('addedAriaLabel')} />
      ) : null}
    </button>
  )
}
